import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const origin = "https://cargopro.ai";

const BASE_KEYWORDS = [
  "CargoPro",
  "TMS",
  "transport management system",
  "logistics software",
  "freight management",
  "supply chain visibility",
  "AI-powered TMS",
  "India logistics",
  "digital logistics",
  "route optimization",
  "carrier management",
  "shipment tracking",
  "RFQ",
  "e-auction",
];

const routeKeywordsMap = new Map([
  [
    "/",
    [
      ...BASE_KEYWORDS,
      "AI-powered transport management system",
      "manufacturers",
      "manufacturing plants",
      "plant logistics",
      "gate automation",
      "yard management",
      "weighbridge automation",
      "dispatch optimization",
      "logistics automation",
      "verified loads",
      "zero commission",
      "load matching",
      "real-time tracking",
    ],
  ],
  [
    "/freight-sourcing-and-reverse-auction",
    [
      "contract auction",
      "spot auction",
      "e-auction",
      "RFQ automation",
      "logistics procurement",
      "carrier bidding",
      "spot bidding",
      "tendering",
    ],
  ],
  [
    "/indent-automation",
    [
      "indent automation",
      "load indenting",
      "vehicle request automation",
      "dispatch request",
      "indent management",
    ],
  ],
  [
    "/invoicing",
    [
      "automated invoicing",
      "invoice reconciliation",
      "billing automation",
      "freight invoice",
    ],
  ],
  [
    "/in-plant-vehicle-movement-and-optimization",
    [
      "in-plant optimization",
      "yard management",
      "vehicle movement",
      "gate-in gate-out",
      "in-plant tracking",
    ],
  ],
  [
    "/proof-of-delivery",
    ["proof of delivery", "ePOD", "digital pod", "POD automation"],
  ],
  [
    "/dashboard-analytics",
    [
      "logistics analytics",
      "dashboard",
      "KPIs",
      "reports",
      "cost optimization",
    ],
  ],
  [
    "/tracking",
    [
      "real-time tracking",
      "GPS tracking",
      "SIM tracking",
      "shipment tracking",
      "ETA",
    ],
  ],
  [
    "/exim",
    [
      "EXIM",
      "import export logistics",
      "import logistics",
      "export logistics",
      "inland haulage",
      "port logistics",
      "ICD",
      "CFS",
      "shipment visibility",
      "freight tracking",
      "freight invoice",
    ],
  ],
  [
    "/dispatch-planning",
    ["dispatch planning", "route planning", "load planning", "fleet planning"],
  ],
  [
    "/features",
    [
      "TMS features",
      "transport management system features",
      "logistics automation features",
      "freight tracking features",
      "plant logistics automation",
    ],
  ],
  [
    "/industries",
    [
      "industries",
      "manufacturing logistics",
      "retail logistics",
      "3PL",
      "courier parcel",
      "D2C",
      "supply chain",
    ],
  ],
  [
    "/about",
    [
      "About CargoPro",
      "logistics SaaS",
      "TMS provider",
      "AI-powered TMS",
      "manufacturing logistics",
      "plant logistics",
      "in-plant visibility",
    ],
  ],
  [
    "/careers",
    [
      "careers",
      "jobs",
      "hiring",
      "logistics tech",
      "TMS jobs",
      "SaaS jobs",
      "B2B SaaS careers",
    ],
  ],
  ["/contacts", ["contact", "sales", "demo", "support"]],
  ["/privacy-policy", ["privacy policy"]],
  ["/terms-and-conditions", ["terms and conditions"]],
  ["/faq", ["faq", "help", "support"]],
  [
    "/blogs",
    [
      "logistics blog",
      "tms blog",
      "supply chain insights",
      "freight tips",
      "dispatch optimization",
      "in-plant visibility",
      "yard management",
      "gate automation",
    ],
  ],
]);

function isBlogPath(pathname) {
  return pathname.startsWith("/blogs/");
}

function isKnownNonBlogPath(pathname) {
  return routeKeywordsMap.has(pathname) && !isBlogPath(pathname);
}

function keywordsForPath(pathname) {
  if (isKnownNonBlogPath(pathname)) {
    return routeKeywordsMap.get(pathname);
  }
  // For blog pages, let the page define its own SEO.
  if (isBlogPath(pathname)) return null;
  // Unknown paths (likely 404 or legacy) should not be indexed.
  return null;
}

export default function RouteSEO() {
  const { pathname, search } = useLocation();
  const url = `${origin}${pathname}${search || ""}`;

  const { robots, includeImages, includeOG } = useMemo(() => {
    const isBlog = isBlogPath(pathname);
    const known = isKnownNonBlogPath(pathname);

    const isBlogSearch = (() => {
      if (pathname !== "/blogs") return false;
      if (!search) return false;
      const params = new URLSearchParams(search);
      return params.has("q");
    })();

    let robotsValue = null;
    let includeImagesMeta = false;
    if (known) {
      robotsValue = "index, follow";
      includeImagesMeta = true;
      if (isBlogSearch) {
        robotsValue = "noindex, follow";
        includeImagesMeta = false;
      }
    } else if (isBlog) {
      // Blog pages manage their own meta; do not duplicate robots or images.
      robotsValue = null;
      includeImagesMeta = false;
    } else {
      // Unknown path -> noindex to avoid indexing 404/legacy catch-alls
      robotsValue = "noindex, nofollow";
      includeImagesMeta = false;
    }

    return {
      robots: robotsValue,
      includeImages: includeImagesMeta,
      includeOG: known && !isBlogSearch,
    };
  }, [pathname, search]);

  return (
    <Helmet>
      {robots ? <meta name="robots" content={robots} /> : null}
      {includeOG ? (
        <>
          <meta property="og:site_name" content="CargoPro" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={url} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@CargoPro" />
        </>
      ) : null}
      {includeImages ? (
        <>
          <meta property="og:image" content={`${origin}/CargoProLogo.webp`} />
          <meta name="twitter:image" content={`${origin}/CargoProLogo.webp`} />
        </>
      ) : null}
    </Helmet>
  );
}
